/* ============================================================
   LEGAL — behaviors for /policies/* pages.
   The policy text is the store's registered policy, verbatim; this
   module only promotes its own <strong> section lines into real
   headings (same characters, better bones), then builds the ledger
   spine: a contents rail whose indigo tick glides to the section
   you are reading. Three or more sections earn the rail; short
   policies keep just the typography. Honors reduced motion.
   ============================================================ */

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const body = document.querySelector('.shopify-policy__body');
const container = document.querySelector('.shopify-policy__container');

if (body && container) {
  /* ---------- promote head-like <strong> lines: <br><strong>X</strong><br> ---------- */
  const meaningful = (n, dir) => {
    let s = dir === 'prev' ? n.previousSibling : n.nextSibling;
    while (s && s.nodeType === 3 && !s.textContent.trim()) s = dir === 'prev' ? s.previousSibling : s.nextSibling;
    return s;
  };
  const isBr = n => n && n.nodeType === 1 && n.tagName === 'BR';
  const heads = [];
  [...body.querySelectorAll('strong')].forEach(st => {
    const text = st.textContent.trim();
    if (!text || text.length > 90) return;
    const prev = meaningful(st, 'prev'), next = meaningful(st, 'next');
    if ((isBr(prev) || prev === null) && isBr(next)) {
      const h = document.createElement('h2');
      h.className = 'legal-h';
      h.id = `sec-${heads.length + 1}`;
      h.textContent = text;
      st.replaceWith(h);
      if (isBr(meaningful(h, 'next'))) meaningful(h, 'next').remove();
      heads.push(h);
    }
  });

  /* ---------- the ledger spine ---------- */
  if (heads.length >= 3) {
    container.classList.add('has-rail');
    const rail = document.createElement('aside');
    rail.className = 'legal-rail';
    rail.setAttribute('aria-label', 'Contents');
    rail.innerHTML = `
      <p class="label legal-rail-label">Contents</p>
      <ol>${heads.map(h => `<li><a href="#${h.id}"><span>${h.textContent}</span></a></li>`).join('')}</ol>
      <span class="legal-tick" aria-hidden="true"></span>`;
    container.prepend(rail);

    /* the spine, folded: a native Contents disclosure for narrow screens */
    const contents = document.createElement('details');
    contents.className = 'legal-contents';
    contents.innerHTML = `
      <summary><span class="label">Contents</span></summary>
      <ol>${heads.map(h => `<li><a href="#${h.id}"><span>${h.textContent}</span></a></li>`).join('')}</ol>`;
    contents.addEventListener('click', e => { if (e.target.closest('a')) contents.open = false; });
    container.insertBefore(contents, container.querySelector('.shopify-policy__body'));

    const items = [...rail.querySelectorAll('li')];
    const tick = rail.querySelector('.legal-tick');

    /* a spine taller than the window glides at its own slower rate,
       so its last entry and the policy's last line arrive together */
    const drift = { overflow: 0, s0: 0, s1: 1, baseTop: 0 };
    const absTop = el => { let y = 0; for (let n = el; n; n = n.offsetParent) y += n.offsetTop; return y; };
    const measureDrift = () => {
      rail.style.top = '';
      drift.baseTop = parseFloat(getComputedStyle(rail).top) || 104;
      /* the spine ends where the text ends: reserve the body's own bottom
         breathing room so the last entry never rides down to the footer */
      const bodyPad = parseFloat(getComputedStyle(body).paddingBottom) || 0;
      const available = innerHeight - drift.baseTop - Math.max(24, bodyPad);
      drift.overflow = Math.max(0, rail.offsetHeight - available);
      drift.s0 = absTop(rail) - drift.baseTop;
      drift.s1 = Math.max(drift.s0 + 1, absTop(container) + container.offsetHeight - innerHeight);
    };

    let current = -1, frame = 0;
    const setCurrent = i => {
      if (i === current) return;
      current = i;
      items.forEach((li, j) => {
        li.classList.toggle('is-current', j === i);
        const a = li.querySelector('a');
        if (j === i) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
      });
      if (i >= 0) {
        const a = items[i].querySelector('a');
        tick.style.transform = `translateY(${a.offsetTop + a.offsetHeight / 2 - tick.offsetHeight / 2}px)`;
        rail.classList.add('is-live');
      } else {
        rail.classList.remove('is-live');
      }
    };
    const check = () => {
      frame = 0;
      const line = innerHeight * 0.38;
      let active = -1;
      heads.forEach((h, i) => { if (h.getBoundingClientRect().top <= line) active = i; });
      setCurrent(active);
      if (drift.overflow > 0) {
        const p = Math.min(1, Math.max(0, (scrollY - drift.s0) / (drift.s1 - drift.s0)));
        rail.style.top = `${Math.round(drift.baseTop - drift.overflow * p)}px`;
      } else if (rail.style.top) {
        rail.style.top = '';
      }
    };
    addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(check); }, { passive: true });
    addEventListener('resize', () => { if (!frame) frame = requestAnimationFrame(() => { measureDrift(); check(); }); }, { passive: true });
    addEventListener('load', () => { measureDrift(); check(); }, { once: true });
    measureDrift();
    check();
  }

  /* ---------- reveals: the section heads arrive the house way ---------- */
  if (heads.length) {
    const io = new IntersectionObserver(es => es.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }), { rootMargin: '0px 0px -12% 0px' });
    heads.forEach(h => { h.classList.add('reveal'); if (reduced) h.classList.add('is-in'); else io.observe(h); });
  }
}
